import {IcnCopy} from "../../assets/icons";

import {useCallback, useEffect, useState} from 'react';
import {Form, Formik} from "formik";
import * as Yup from "yup";
import {QRCodeSVG} from "qrcode.react";
import {Slide, toast} from "react-toastify";

import {useCredit} from "../../providers/CreditStatusProvider";
import {HomePayFeeFormStablecoinValues} from "../../utils";
import {AvailableBalance, payFees} from "../../utils/creditService";

import FormFieldset from "../form-elements/FormFieldset";
import FormInput from "../form-elements/FormInput";
import FormInputNumber from "../form-elements/FormInputNumber";
import BlockToken from "../common/BlockToken";
import Button from "../common/Button";
import FormGroupHelpText from "../form-elements/FormGroupHelpText";
import {ethAddressValidator} from "../../utils/ethers";

const HomePayFeeFormStablecoinSchema = Yup.object().shape({
  amount: Yup.number().required('Please enter an amount to proceed.'),
  tokens: Yup.array().of(Yup.string()).notRequired(),
  walletAddress: Yup.string()
    .test('is-valid-eth-address', 'Invalid Ethereum wallet address', ethAddressValidator)
    .required('Ethereum wallet address is required'),
})

interface HomePayFeeFormStablecoinProps {
  creditLine: AvailableBalance;
}

const networks2icon: { [key: string]: string } = {
  "ETHEREUM_MAINNET": "ETH",
  "POLYGON_MAINNET": "MATIC",
  "POLYGON_AMOY": "MATIC",
  "ETHEREUM_TESTNET": "ETH",
  "OPTIMISM_SEPOLIA": "OP",
  "OPTIMISM_MAINNET": "OP",
  "BASE_MAINNET": "BASE",
  "BASE_SEPOLIA": "BASE",
  "SOLANA_MAINNET": "SOL",
  "SOLANA_DEVNET": "SOL",
}

function HomePayFeeFormStablecoin({ creditLine }: HomePayFeeFormStablecoinProps) {
  const { creditFeesOwed, setSelectedCreditLineId } = useCredit();
  const [qrCodeValue, setQrCodeValue] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(true);

  // Update the selected credit line ID to match the current credit line
  useEffect(() => {
    if (creditLine?.id) {
      setSelectedCreditLineId(creditLine.id);
    }
  }, [creditLine?.id, setSelectedCreditLineId]);

  // Debug logging
  console.log('[HomePayFeeFormStablecoin] creditFeesOwed:', creditFeesOwed);
  console.log('[HomePayFeeFormStablecoin] creditLine:', creditLine);

  const [formData, setFormData] = useState<HomePayFeeFormStablecoinValues>({
    amount: creditFeesOwed,
    tokens: ["USDC", networks2icon[creditLine.network] || "NA"],
    walletAddress: "",
  })

  console.log('[HomePayFeeFormStablecoin] formData:', formData);

  // Fetch the repayment address when selected credit line changes
  useEffect(() => {
    const fetchRepaymentAddress = async () => {
      if (!creditLine?.id) return;
      try {
        const response = await payFees(creditLine.id);
        setFormData((prevData) => ({
          ...prevData,
          walletAddress: response.repayment_address,
        }));
        setQrCodeValue(response.repayment_address);
      } catch (error) {
        toast.error("Unable to fetch repayment address. Please try again.");
      } finally {
        setSubmitting(false);
      }
    };
    
    fetchRepaymentAddress();
  }, [creditLine.id]);



  const token_currency = formData.tokens[0];

  const handleCopyToClipboard = useCallback((value: string) => {
    navigator.clipboard.writeText(qrCodeValue)
      .then(() => {
        toast.success('Copied to clipboard', {
          toastId: value,
          position: "bottom-right",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          transition: Slide,
        })
      })
      .catch(err => {
        console.error('Failed to copy text: ', err)
        toast.error('Failed to copy to clipboard. Please try again.', {
          toastId: value,
          position: "bottom-right",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          transition: Slide,
        })
      })
  }, [qrCodeValue])

  return (
    <Formik
      initialValues={formData}
      validationSchema={HomePayFeeFormStablecoinSchema}
      onSubmit={(values, { setSubmitting, setStatus }) => {
        setTimeout(() => {
          setSubmitting(false);
          setStatus('submitted'); // Set form status as 'submitted'
        }, 400);
      }}
    >
      {({ isSubmitting, values, status, errors, isValid, touched }) => (
        <Form
          className="c-form"
          noValidate
        >
          <FormFieldset
            extraClassName="amount"
            legendText='Verify due credit fees'
          >
            <FormInputNumber
              extraClassName="boxed"
              formGroupStyle="block-line"
              labelText="Credit fees due"
              name="creditFee"
              value={values.amount}
              placeholder={`0.0000 ${values.tokens? token_currency : ""}`}
              inputGroupText="$ "
              inputGroupPlacement='left'
              hideError
              decimalScale={4}
              fixedDecimalScale
              suffix={` ${values.tokens? token_currency : ""}`}
              disabled
              readOnly
              formGroupExtraContent={
                <div className="c-form-fieldset-block-token-list">
                  {values.tokens.map((token, tokenIdx) => (
                    <BlockToken
                      key={tokenIdx}
                      token={token}
                      hasName
                    />
                  ))}
                </div>
              }
            />

          </FormFieldset>

          <FormFieldset
            extraClassName="qr-code"
            legendText='Pay via QR code'
          >
            <div className="c-form-fieldset-qr-code">
                <div className="c-form-fieldset-qr-code-wrapper">
                  {submitting ? (
                    <div className="c-loader"/>
                  ) : qrCodeValue ? (
                      <QRCodeSVG
                      value={qrCodeValue}
                      title="Repayment Address"
                      size={205}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="L"
                    />
                  ) : (
                    <p className="no-qr-message">QR code is not available. Please try again later.</p>
                  )}
              </div>

            </div>

          </FormFieldset>

          <div className="c-form-fieldset-separator">
            <span>or</span>
          </div>

          <FormFieldset
            extraClassName="cta"
            legendText='Pay via wallet address'
          >
            <FormInput
              extraClassName="boxed"
              formGroupStyle="block-line"
              labelText="Wallet Address"
              type="text"
              name="walletAddress"
              value={qrCodeValue}
              disabled
              readOnly
              formGroupExtraContent={
                <Button
                  extraClassName="clipboard-copy-btn"
                  title="Copy to clipboard"
                  btnVariant="icon"
                  btnColor="ghost"
                  icon={<IcnCopy/>}
                  onClick={() => handleCopyToClipboard(values.walletAddress)}
                />
              }
            />

            <FormGroupHelpText helpText="Please only send USDC to the wallet address above."/>

          </FormFieldset>
        </Form>
      )}

    </Formik>
  );
}

export default HomePayFeeFormStablecoin;